import apiClient from '@/services/api/apiClient';

export interface ProspectingCriteria {
  mode?: 'BUYER' | 'PARTNER';
  partnerType?: string;
  territory?: string;
  targetIndustry: string;
  region: string;
  sizeMin: number;
  sizeMax: number;
  revenueRange?: string[];
  keywords: string[];
  techStack?: string[];
  intentTopics?: string[];
  buyingSignals?: string[];
  maxResults: number;
  connectors?: Record<string, string>;
}

export interface ConnectorSelection {
  discovery:  string;
  enrichment: string;
  email:      string;
  llm:        string;
  linkedin:   string;
  webhook:    string;
}

export interface ConnectorDef {
  id: string;
  label: string;
  category: string;
  description: string;
  docsUrl: string | null;
  fields: { key: string; label: string; type: string; placeholder: string }[];
  isMock: boolean;
  isConfigured: boolean;
  isActive: boolean;
}

export interface AgentRunLog {
  id: string;
  runId: string;
  agentName: string;
  batchId: string | null;
  inputJson: any;
  outputJson: any;
  status: string;
  modelsUsed: string[];
  totalTokens: number | null;
  totalCostUsd: number | null;
  latencyMs: number | null;
  errorLog: any;
  triggeredById: string;
  triggeredBy?: { id: string; name: string };
  createdAt: string;
}

export interface Prospect {
  id: string;
  batchId: string;
  companyName: string;
  accountType?: string;
  partnerType?: string | null;
  partnerTerritory?: string | null;
  partnerMarketFocus?: string | null;
  partnerPortfolio?: string | null;
  partnerFitRationale?: string | null;
  domain: string | null;
  linkedInUrl: string | null;
  region: string | null;
  estimatedSize: string | null;
  industryVertical: string | null;
  primaryPainAxis: string | null;
  painRationale: string | null;
  maturityBand: string | null;
  architectureTags: string[];
  icpScoreEstimate: number | null;
  winTheme: string | null;
  cantieroCases: string[];
  sourceUrls: string[];
  signals: string[];
  keyContactName: string | null;
  keyContactEmail: string | null;
  keyContactPhone: string | null;
  keyContactTitle: string | null;
  keyContactLinkedIn: string | null;
  keyContactDept: string | null;
  additionalContacts?: { name?: string; email?: string; phone?: string; title?: string; dept?: string; linkedin?: string }[] | null;
  outreachEmailSubj: string | null;
  outreachEmailBody: string | null;
  outreachCallScript: string | null;
  outreachLinkedInDm: string | null;
  reviewStatus: 'PENDING' | 'APPROVED' | 'REJECTED' | 'EDITED';
  reviewedById: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
  createdAccountId: string | null;
  createdContactId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProspectBatch {
  id: string;
  criteria: ProspectingCriteria & { connectors?: ConnectorSelection };
  status: 'PENDING' | 'RUNNING' | 'REVIEW' | 'APPROVED' | 'FAILED';
  triggeredById: string;
  triggeredBy?: { id: string; name: string; email: string };
  totalFound: number;
  totalApproved: number;
  totalRejected: number;
  agentRunId: string | null;
  errorLog: any;
  createdAt: string;
  updatedAt: string;
  prospects?: Prospect[];
  _count?: { prospects: number };
}

// ── Connector API ────────────────────────────────────────────────────────────
export const getConnectors = async () => {
  const { data } = await apiClient.get('/api/prospecting/connectors');
  return data.connectors as ConnectorDef[];
};

export const saveConnectorConfig = async (id: string, config: Record<string, string>, label?: string) => {
  const { data } = await apiClient.post(`/api/prospecting/connectors/${id}/configure`, { config, label, isActive: true });
  return data;
};

export const deleteConnectorConfig = async (id: string) => {
  await apiClient.delete(`/api/prospecting/connectors/${id}/configure`);
};

// ── Run API ──────────────────────────────────────────────────────────────────
export const startProspectingRun = async (criteria: ProspectingCriteria, connectors: ConnectorSelection) => {
  const { data } = await apiClient.post('/api/prospecting/run', { ...criteria, connectors });
  return data as { batchId: string; status: string; message: string };
};

export const getBatches = async (page = 1, limit = 20) => {
  const { data } = await apiClient.get('/api/prospecting/batches', { params: { page, limit } });
  return data as { data: ProspectBatch[]; total: number; page: number; totalPages: number };
};

export const getBatch = async (id: string) => {
  const { data } = await apiClient.get(`/api/prospecting/batches/${id}`);
  return data as ProspectBatch;
};

export const getBatchStatus = async (id: string) => {
  const { data } = await apiClient.get(`/api/prospecting/batches/${id}/status`);
  return data;
};

export const updateProspect = async (id: string, updates: Partial<Prospect>) => {
  const { data } = await apiClient.patch(`/api/prospecting/prospects/${id}`, updates);
  return data as Prospect;
};

export const deleteBatch = async (id: string) => {
  await apiClient.delete(`/api/prospecting/batches/${id}`);
};

export const approveBatch = async (batchId: string, approvedIds: string[]) => {
  const { data } = await apiClient.post(`/api/prospecting/batches/${batchId}/approve`, { approvedIds });
  return data as { approved: number; failed: number; results: any[]; errors: any[]; message: string };
};

export const enrichContact = async (prospectId: string) => {
  const response = await apiClient.post(`/api/prospecting/prospects/${prospectId}/enrich-contact`);
  return response.data;
};

// ── Single-account enrichment ────────────────────────────────────────────────
export interface AccountEnrichment {
  id: string;
  name: string;
  enrichmentStatus: 'RUNNING' | 'DONE' | 'FAILED' | null;
  enrichmentRunId: string | null;
  enrichedAt: string | null;
  analyzedPainAxis: string | null;
  analyzedPainRationale: string | null;
  analyzedMaturityBand: string | null;
  analyzedIcpScore: number | null;
  analyzedWinTheme: string | null;
  analyzedArchitectureTags: string[];
  analyzedCantieroCases: string[];
  analyzedSignals: string[];
  analyzedSourceUrls: string[];
  analyzedOutreachKit: {
    emailSubject: string | null;
    emailBody: string | null;
    callScript: string | null;
    linkedInDm: string | null;
  } | null;
  analyzedEstimatedSize: string | null;
  analyzedCompetitiveAngle: string | null;
  analyzedDataReadiness: string | null;
  analyzedAutomationStage: string | null;
  analyzedPlantType: string | null;
  analyzedLinkedInUrl: string | null;
  analyzedContact: {
    name: string | null;
    title: string | null;
    dept: string | null;
    email: string | null;
    phone: string | null;
    linkedIn: string | null;
  } | null;
  // human-entered, for side-by-side compare
  primaryPainAxis: string | null;
  maturityBand: string | null;
  plantType: string | null;
}

export const enrichAccount = async (accountId: string, connectors: Partial<ConnectorSelection>) => {
  const { data } = await apiClient.post(`/api/prospecting/enrich-account/${accountId}`, { connectors });
  return data as { accountId: string; runId: string; status: string; message: string };
};

export const getAccountEnrichmentStatus = async (accountId: string) => {
  const { data } = await apiClient.get(`/api/prospecting/enrich-account/${accountId}/status`);
  return data as AccountEnrichment;
};

export const promoteAccountEnrichment = async (accountId: string, fields: string[]) => {
  const { data } = await apiClient.post(`/api/prospecting/enrich-account/${accountId}/promote`, { fields });
  return data as { promoted: string[]; account: any };
};
